/**
 * The jukebox on /music -- an iPod running Cover Flow, like the real ones did. The page
 * arrives with the photograph, the screen overlay (title bar, the flow of sleeves, the
 * song title line, the progress row) and every caption server-rendered; this file adds
 * the playing: the shared <audio>, the wheel keys over the printed glyphs, the fanned
 * transforms (sized off the flow's own width, so the same math fits the little LCD),
 * and the loop toggle in the title bar.
 *
 * Loop has exactly two states: 'all' (the default -- the setlist wraps forever) and
 * 'one' (this song again and again). There is no 'off'; a jukebox does not stop.
 */

const flow = document.querySelector('#screenflow');
if (flow) {
  const sleeves = [...flow.querySelectorAll('.flow__sleeve')];
  const captions = [...document.querySelectorAll('.caption')];
  const player = document.querySelector('#player');
  const songtitle = document.querySelector('#songtitle');
  let current = 0;

  /* -------------------------------------------------------------------- Flow */

  /** The Finder fan, in units of the flow's width so it fits whatever the LCD gives. */
  const layout = () => {
    const W = flow.clientWidth;
    const H = flow.clientHeight;
    const size = Math.min(0.34 * W, 0.85 * H);
    sleeves.forEach((sleeve, i) => {
      const d = i - current;
      const side = Math.sign(d);
      const x = d * 0.105 * W + side * 0.16 * W;
      const rot = side * -58;
      const z = d === 0 ? 0.2 * W : -Math.min(Math.abs(d), 4) * 0.04 * W;
      sleeve.style.width = `${size}px`;
      sleeve.style.height = `${size}px`;
      sleeve.style.margin = `${-size / 2}px 0 0 ${-size / 2}px`;
      sleeve.style.transform = `translateX(${x}px) translateZ(${z}px) rotateY(${rot}deg)`;
      sleeve.style.zIndex = String(100 - Math.abs(d));
      sleeve.classList.toggle('flow__sleeve--front', d === 0);
      sleeve.setAttribute('aria-current', d === 0 ? 'true' : 'false');
    });
  };

  window.addEventListener('resize', layout);

  const go = (index, { play = true } = {}) => {
    current = (index + sleeves.length) % sleeves.length; // the setlist is a circle
    layout();
    songtitle.textContent = sleeves[current].dataset.title;
    captions.forEach((c, i) => c.classList.toggle('caption--on', i === current));
    const src = sleeves[current].dataset.audio;
    if (!player.getAttribute('src')?.endsWith(src)) {
      player.src = src;
    }
    // Autoplay can be refused until the visitor has touched the page; the catch keeps
    // that refusal silent, and the next key press counts as the touch.
    if (play) player.play().catch(() => {});
  };

  document.querySelector('#prev').addEventListener('click', () => go(current - 1));
  document.querySelector('#next').addEventListener('click', () => go(current + 1));
  sleeves.forEach((sleeve, i) => sleeve.addEventListener('click', () => go(i)));

  // Arrow keys thumb too, unless something focusable (a dialog input) has the keyboard.
  document.addEventListener('keydown', e => {
    const tag = document.activeElement?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || document.querySelector('dialog[open]')) return;
    if (e.key === 'ArrowLeft') go(current - 1);
    if (e.key === 'ArrowRight') go(current + 1);
  });

  /* ------------------------------------------------------------------ Screen */

  const playKey = document.querySelector('#play');
  const elapsed = document.querySelector('#elapsed');
  const remaining = document.querySelector('#remaining');
  const bar = document.querySelector('#bar');
  const fill = document.querySelector('#fill');

  const clock = secs => {
    if (!Number.isFinite(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  /** The iPod readout: time gone on the left, time left on the right. */
  const paint = () => {
    elapsed.textContent = clock(player.currentTime);
    remaining.textContent = `-${clock(Math.max(0, (player.duration || 0) - player.currentTime))}`;
    const frac = player.duration ? player.currentTime / player.duration : 0;
    fill.style.width = `${frac * 100}%`;
  };

  player.addEventListener('timeupdate', paint);
  player.addEventListener('loadedmetadata', paint);

  playKey.addEventListener('click', () => {
    if (player.paused) player.play().catch(() => {});
    else player.pause();
  });

  // Scrubbing: click lands the needle, and holding the button down drags it.
  const seekTo = e => {
    const rect = bar.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    if (player.duration) player.currentTime = frac * player.duration;
    paint();
  };
  bar.addEventListener('pointerdown', e => {
    bar.setPointerCapture(e.pointerId);
    seekTo(e);
  });
  bar.addEventListener('pointermove', e => {
    if (bar.hasPointerCapture(e.pointerId)) seekTo(e);
  });

  /* -------------------------------------------------------------------- Loop */

  const loopKey = document.querySelector('#loop');
  let loopMode = 'all';

  loopKey.addEventListener('click', () => {
    loopMode = loopMode === 'all' ? 'one' : 'all';
    loopKey.dataset.mode = loopMode;
    loopKey.setAttribute('aria-label', loopMode === 'all' ? 'looping all songs' : 'looping this song');
  });

  player.addEventListener('ended', () => {
    if (loopMode === 'one') {
      player.currentTime = 0;
      player.play().catch(() => {});
    } else {
      go(current + 1); // go() wraps, so the last song rolls back to the first
    }
  });

  go(0);
}
